import { NgZone } from '@angular/core';
import { Injectable } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { NavController, Platform } from '@ionic/angular';
import { interval, Subject, Subscription } from 'rxjs';
import { DeviceProvider } from '../../core-modules/providers/device/device';
import { EnvironmentProvider } from '../../core-modules/providers/environment-variables/environment';
import { DeeplinkApplicationError } from '../../errors/business-objects/application-error.deeplink';
import { DeepLinkRoutes } from '../business-objects/deeplink-routes';

/*
  Generated class for the CoreDialogController provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular 2 DI.
*/
@Injectable()
export class CoreApplication
{
    //#region Constructor

    /**
     * Class constructor
     * @param platform ionic platform provider
     * @param device device provider
     * @param env environment variables provider
     * @param zone Angular zone
     * @param navCtrl Navigation controller
     */
    constructor
        (
            private platform: Platform,
            private device: DeviceProvider,
            private env: EnvironmentProvider,
            private zone: NgZone,
            private navCtrl: NavController
        )
    {
        this.pauseTimestamp = Date.now();
        this.requestLogin = new Subject<any>();

        // start the timer
        let timer = this.registerTimer();

        // register the processResumeEvent as the handler for the resume subscription
        this.platform.resume.subscribe(() =>
        {
            this.processResumeEvent();
        });

        // register the application deeplinks
        this.registerDeeplinks();
    }

    //#endregion


    //#region Properties

    /**
     * Timestamp of when pause events occurred
     */
    private pauseTimestamp: number;

    /**
     * Duration threshold  between pause and resume events
     */
    readonly PAUSE_MILLISECONDS_THRESHOLD = (5 * 60 * 1000); //5 mins

    /**
     * Ticker interval duration
     */
    readonly PAUSE_MILLISECONDS_TICKER = (30 * 1000); //

    /**
     * Observable which requests showing of the pin
     */
    public requestLogin: Subject<any>;

    //#endregion


    //#region Methods - Provider Initialization

    /**
     * Register the application deeplinks
     */
    private registerDeeplinks()
    {
        App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) =>
        {
            await this.zone.run(async () =>
            {
                // On iOS devices, the browser does not close itself whenever a deeplink or applink is 
                // triggerred, therefore we are checking whether the device is an iOS device and if yes
                // we are trying to close any browser tabs. 
                // The plugin will throw an exception if there are no tabs to close hence the try/catch
                if (this.device.isiOS())
                {
                    try 
                    {
                        // hide the SafariViewController
                        await Browser.close();
                    }
                    catch {

                    }
                }

                let handledURL = this.handleUniversalLink(event.url, this.env.appVersion);

                // starting app with universal links skips landing page hide splashscreen 
                SplashScreen.hide();

                // determining route
                // handling of urls. app will catch anything under the portal domain as long as user selects app for it.
                // below are the urls that are being handled.
                switch (handledURL.route)
                {
                    case DeepLinkRoutes.LOG_IN:
                        {
                            this.processRoute('/azureb2c-login-entry', handledURL.arg);
                            break;
                        }
                    case DeepLinkRoutes.CREATE_ACCOUNT:
                        {
                            this.processRoute('/azureb2c-create-account-entry', handledURL.arg);
                            break;
                        }
                    case DeepLinkRoutes.ACCOUNT_LOGIN:
                        {
                            this.processRoute('/account-login', handledURL.arg);
                            break;
                        }
                    case DeepLinkRoutes.MESSAGE:
                        {
                            this.processRoute('/message', handledURL.arg);
                            break;
                        }
                    case DeepLinkRoutes.ERROR:
                        {
                            this.processRoute('/error', handledURL.arg);
                            break;
                        }
                    default:
                        {
                            // error.handleError is used as throw is not caught by our error handler
                            throw new DeeplinkApplicationError(this.device.getDeviceType(), this.env.appVersion, this.device.getDeviceSoftwareVersion(), this.device.getDeviceUserAgent(), event.url);
                        }
                }
            });
        });
    }

    //#endregion


    //#region Methods - Requesting of PIN login

    /**
     * Register the observable timer
     */
    private registerTimer(): Subscription
    {
        return interval(this.PAUSE_MILLISECONDS_TICKER) // 30 seconds
            .subscribe(() =>
            {
                let now = Date.now();

                if ((now - this.pauseTimestamp) < this.PAUSE_MILLISECONDS_THRESHOLD)
                {
                    this.pauseTimestamp = now;
                }
            });
    }

    /**
     * Handle the resume event
     * @return true if the duration between pause and resume exceeds the stipulated threshold
     */
    private processResumeEvent(): void
    {
        let now = Date.now();

        // check whether the difference between now and the last pause is greater than the
        // amount defined in PAUSE_SECONDS_THRESHOLD
        if ((now - this.pauseTimestamp) > this.PAUSE_MILLISECONDS_THRESHOLD)
        {
            this.requestLogin.next();
        }

        this.pauseTimestamp = now;
    }

    //#endregion


    //#region Methods - Deep Links

    /**
     * Formats return url from MobileAppAPI
     * @param url Return URL given by api
     * @param application version
     * @return object { route: routing path for app , param: arguments such as state and token }
     */
    private handleUniversalLink(url: string, appVersion: string): { route: string; arg: { [id: string]: any }; }
    {
        try
        {
            // specify regex for extracting routes
            let routeRegex = /.+?\:\/\/.+?(\/.+?)(?:#|\?|$)/;
            let route = routeRegex.exec(url);

            //split return url into routing path and arguments/parameters
            let queryString = url.split("?");

            // specify result object
            let params = {};
            let queryStringVariables = (queryString[1] ? queryString[1].split('&') : null);

            if (queryStringVariables)
            {
                // parse the querystring
                for (let i = 0; i < queryStringVariables.length; i++)
                {
                    let splitString = queryStringVariables[i].split('=');
                    params[decodeURIComponent(splitString[0])] = decodeURIComponent(splitString[1]);
                }
            }

            return { route: route[1], arg: params }
        } catch
        {
            // if not valid log error
            // error.handleError is used as throw is not caught by our error handler
            throw new DeeplinkApplicationError(this.device.getDeviceType(), appVersion, this.device.getDeviceSoftwareVersion(), this.device.getDeviceUserAgent(), url);
        }
    }

    /**
     * Process the route reached
     * @param target destination
     * @param args arguments to pass to page
     */
    private processRoute(target: string, args: any): void
    {
        // use the args object as the queryParams value 
        // and forward it to the respective page in the application
        // so that the parameter values are not lost from the original url 
        let navigationExtras: NavigationExtras = {
            queryParams:args
        };

        this.navCtrl.navigateForward(target, navigationExtras);
    }

    //#endregion
}